import { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useCardSnapshot } from "@/hooks/useCardSnapshot";
import { getCategoryPreset } from "@/lib/categoryConfig";
import { useAdminCopy } from "@/contexts/AdminCopyContext";
import ShareThreeOptions from "@/components/ShareThreeOptions";
import {
  ArrowUpFromLine,
  Copy,
  Edit3,
  Eye,
  Utensils,
  Accessibility,
  Check,
  User,
  Building,
  Sparkles,
  Calendar as CalendarIcon,
  Loader2,
  Plus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preference Card Visual Preview Component (large preview at top of modal)
export const PreferenceCardPreview = ({
  user,
  userDefaults,
  event = null,
  compact = false,
  cardColor = "#000000",
  experience = null,
}) => {
  if (!user) return null;

  const dietary = userDefaults?.dietary || {};
  const accessibility = userDefaults?.accessibility || {};
  const events = userDefaults?.events || {};

  const dietaryRestrictions = dietary.restrictions || [];
  const allergies = dietary.allergies || [];
  const accessibilityNeeds = accessibility.needs || [];
  const interests = events.interests || [];

  const hasPreferences =
    dietaryRestrictions.length > 0 ||
    allergies.length > 0 ||
    accessibilityNeeds.length > 0 ||
    interests.length > 0;

  const preset = experience?.category ? getCategoryPreset(experience.category) : null;
  const effectiveColor = preset?.iconColor || cardColor;

  return (
    <div
      className="rounded-2xl overflow-hidden shadow-xl"
      style={{
        background: `linear-gradient(135deg, ${effectiveColor} 0%, ${effectiveColor}dd 100%)`,
      }}
    >
      <div className="p-4 text-white relative">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }} />
        <div className="flex items-center gap-4 relative">
          <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center overflow-hidden flex-shrink-0">
            {user.profile_photo_url ? (
              <img src={user.profile_photo_url} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <User className="w-8 h-8 text-white/70" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-xl truncate">{user.name || "Guest"}</h3>
            {user.title && <p className="text-white/70 text-sm truncate">{user.title}</p>}
            {user.company && (
              <p className="text-white/60 text-xs flex items-center gap-1 mt-0.5">
                <Building className="w-3 h-3" />
                {user.company}
              </p>
            )}
            <p className="text-white/50 text-xs mt-1">{user.wallet_id || "PHY-MEMBER"}</p>
          </div>
        </div>

        {experience && (
          <div className="mt-3 pt-3 border-t border-white/20">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Sparkles className="w-4 h-4" />
              <span className="truncate">{experience.name || preset?.name || "Experience"}</span>
            </div>
          </div>
        )}

        {event && !experience && (
          <div className="mt-3 pt-3 border-t border-white/20">
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <CalendarIcon className="w-4 h-4" />
              <span className="truncate">{event.name}</span>
            </div>
          </div>
        )}
      </div>

      {hasPreferences && !compact && (
        <div className="bg-white/95 backdrop-blur p-4 space-y-3">
          {interests.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Interests
              </p>
              <div className="flex flex-wrap gap-1.5">
                {interests.map(interest => (
                  <Badge key={interest} variant="secondary" className="text-xs bg-purple-50 text-purple-700">
                    {interest}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {(dietaryRestrictions.length > 0 || allergies.length > 0) && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                <Utensils className="w-3 h-3" /> Dietary
              </p>
              <div className="flex flex-wrap gap-1.5">
                {dietaryRestrictions.map(r => (
                  <Badge key={r} variant="secondary" className="text-xs bg-orange-50 text-orange-700">
                    {r.replace(/_/g, ' ')}
                  </Badge>
                ))}
                {allergies.map(a => (
                  <Badge key={a} variant="secondary" className="text-xs bg-red-50 text-red-700">
                    {a} allergy
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {accessibilityNeeds.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                <Accessibility className="w-3 h-3" /> Accessibility
              </p>
              <div className="flex flex-wrap gap-1.5">
                {accessibilityNeeds.map(need => (
                  <Badge key={need} variant="secondary" className="text-xs bg-blue-50 text-blue-700">
                    {need.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
        <span className="text-[10px] text-gray-400">phygital.systems</span>
        <span className="text-[10px] text-gray-400">Preference Card</span>
      </div>
    </div>
  );
};

// Mini preference card tile - kanban-style (3-image inspo strip + meta)
// Iter 9.50+ R16 - upgraded to mirror the host kanban look so the user
// recognises each card from the up-arrow share popup.
const ExperienceMiniCard = ({ exp, user, selected, onClick }) => {
  const preset = getCategoryPreset(exp.category) || { iconColor: "#9c57f5", bgColor: "#f3f4f6", name: "Experience" };
  const displayName = exp.name || exp.title || (preset.name && preset.name !== "Experience" ? preset.name : null) || (exp.category ? `${exp.category.charAt(0).toUpperCase() + exp.category.slice(1)}` : "Experience");
  const inspoUrls = (Array.isArray(exp?.inspo) ? exp.inspo : [])
    .map((i) => (typeof i === "string" ? i : i?.url || i?.image_url))
    .filter(Boolean);
  const inspoCells = [0, 1, 2].map((i) => inspoUrls[i] || null);
  const dateLabel = exp.target_date || exp.experience_date
    ? new Date(exp.target_date || exp.experience_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : exp.linked_event || "TBD";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`share-experience-${exp.experience_id || exp.category || displayName}`}
      className={`group relative rounded-xl overflow-hidden shadow-sm transition-all text-left bg-white border-2 ${
        selected ? "border-purple-500 ring-2 ring-purple-200" : "border-stone-200 hover:border-purple-300"
      }`}
    >
      {/* 3-image inspo strip - same as kanban */}
      <div className="grid grid-cols-3 gap-px bg-stone-100">
        {inspoCells.map((src, i) => (
          <div key={i} className="aspect-square overflow-hidden flex items-center justify-center" style={{ backgroundColor: preset.bgColor || "#f5f5f4" }}>
            {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <Sparkles className="w-3 h-3 text-stone-300" />}
          </div>
        ))}
      </div>
      <div className="p-2.5 flex items-center gap-2">
        <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0" style={{ background: preset.iconColor }}>
          {user?.profile_photo_url ? <img src={user.profile_photo_url} alt="" className="w-full h-full object-cover" /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-stone-900 truncate leading-tight">{displayName}</p>
          <p className="text-[9px] text-stone-500 truncate">{dateLabel}</p>
        </div>
        {selected ? (
          <Check className="w-3.5 h-3.5 text-purple-600" />
        ) : (
          <Eye className="w-3 h-3 text-stone-300 group-hover:text-stone-600" />
        )}
      </div>
    </button>
  );
};

// Main Share Modal Component
export default function SharePreferenceCardModal({
  open,
  onOpenChange,
  user,
  userDefaults,
  event = null,
  cardColor = "#000000",
  onViewPreferences,
  experiences = [],
}) {
  const navigate = useNavigate();
  const t = useAdminCopy();
  const [copied, setCopied] = useState(false);
  const [selectedExp, setSelectedExp] = useState(null);

  const cardSnapshotRef = useRef(null);
  const cardId = selectedExp?.experience_id || user?.preference_card_id || user?.user_id;
  const { captureAndUpload, capturing } = useCardSnapshot(cardSnapshotRef, cardId);
  const [snapshotUrl, setSnapshotUrl] = useState(null);

  const displayExperiences = useMemo(() => {
    const seen = new Set();
    const out = [];
    (experiences || []).forEach(e => {
      const key = e.experience_id || `${e.category}-${e.name}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(e);
    });
    return out.slice(0, 12);
  }, [experiences]);

  useEffect(() => {
    if (open && cardId && cardSnapshotRef.current) {
      const t = setTimeout(async () => {
        const url = await captureAndUpload();
        if (url) setSnapshotUrl(url);
      }, 800);
      return () => clearTimeout(t);
    }
  }, [open, cardId, selectedExp]);

  const getShareUrl = () => {
    if (selectedExp?.experience_id) {
      return `${window.location.origin}/c/${selectedExp.experience_id}`;
    }
    return `${window.location.origin}/profile/${user?.wallet_id || user?.user_id}`;
  };

  const copyLink = () => {
    const url = getShareUrl();
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Share link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareNative = async () => {
    const url = getShareUrl();
    const shareText = selectedExp
      ? `Here's my preference card for ${selectedExp.name || selectedExp.category}`
      : `Check out my preference card`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${user?.name}'s Phygital Preference Card`,
          text: shareText,
          url,
        });
      } catch (err) {
        // Iter 9.30: silently ignore the user-cancelled share (AbortError) - the
        // browser's native share sheet throws on dismiss, which is not an error.
        if (err?.name !== "AbortError") {
          console.warn("Share failed", err);
        }
      }
    } else {
      copyLink();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="p-4 pb-2 border-b sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpFromLine className="w-5 h-5 text-purple-500" data-testid="share-modal-arrow" />
            {t("me.share.modal_title", "Share Your Preference Card")}
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 space-y-4">
          {/* Quick Copy Button */}
          <Button
            onClick={copyLink}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white h-11"
            data-testid="share-quick-copy"
          >
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? t("me.share.copied", "Link copied") : t("me.share.copy_link_cta", "Copy share link")}
          </Button>

          {/* Visual Card Preview (large) */}
          <div className="text-center">
            <p className="text-xs text-gray-500">{t("me.share.preview_caption", "Preview of what others see")}</p>
          </div>
          <div className="mx-auto max-w-[320px]" ref={cardSnapshotRef}>
            <PreferenceCardPreview
              user={user}
              userDefaults={userDefaults}
              event={event}
              cardColor={cardColor}
              experience={selectedExp}
            />
          </div>
          {capturing && (
            <p className="text-[10px] text-gray-400 text-center flex items-center justify-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Generating share preview...
            </p>
          )}
          {snapshotUrl && !capturing && (
            <p className="text-[10px] text-emerald-600 text-center flex items-center justify-center gap-1">
              <Check className="w-3 h-3" /> Share preview ready
            </p>
          )}

          {/* Mini-card grid: pick a card to share */}
          <div className="space-y-2 pt-2 border-t">
            <p className="text-xs font-medium text-gray-600">Pick a card to share</p>

            {displayExperiences.length === 0 ? (
              <button
                onClick={() => {
                  onOpenChange(false);
                  navigate("/me?tab=experiences&plan=1");
                }}
                className="w-full p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all flex items-center justify-center gap-2 text-sm text-gray-600"
                data-testid="share-empty-plan-cta"
              >
                <Plus className="w-4 h-4" />
                Plan an experience to share
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {displayExperiences.map((exp) => (
                  <ExperienceMiniCard
                    key={exp.experience_id || `${exp.category}-${exp.name}`}
                    exp={exp}
                    user={user}
                    selected={selectedExp?.experience_id === exp.experience_id}
                    onClick={() => setSelectedExp(selectedExp?.experience_id === exp.experience_id ? null : exp)}
                  />
                ))}
              </div>
            )}
            {selectedExp && (
              <p className="text-xs text-purple-600 bg-purple-50 p-2 rounded-lg">
                Sharing <span className="font-semibold">{selectedExp.name || selectedExp.category || "this card"}</span>. Recipient sees branding from admin category.
              </p>
            )}
          </div>

          {/* Pass 2 - 3-option share component (replaces single-share CTA per Shelsea handoff) */}
          <ShareThreeOptions
            url={getShareUrl()}
            shareTitle={`${user?.name || "My"} Phygital Preference Card`}
            shareText={selectedExp ? `Here's my preference card for ${selectedExp.name || selectedExp.category}` : `Check out my preference card`}
            className="w-full justify-center pt-2"
            size="lg"
            testId="share-preference-card"
          />

          {onViewPreferences && (
            <Button
              variant="ghost"
              className="w-full text-gray-500"
              onClick={() => {
                onOpenChange(false);
                onViewPreferences();
              }}
              data-testid="edit-preferences"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit My Preferences
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
